﻿import React, { useState, useEffect } from 'react';
import { IcInstrumentData } from '../models/Instruments/icp-instrument-data';
import { Asset } from '../models/asset';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { CrudFunctions } from '../utilities/crud-functions';
import { FieldUpdate } from '../models/field-update';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { BenchSheetTechnologyType, ModalMessageType, IcpBenchSheetType } from '../models/enums';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';

import { SelectBox } from 'devextreme-react/select-box';
import { NumberBox } from 'devextreme-react/number-box';
import { TextBox } from 'devextreme-react/text-box';
import { Button } from 'devextreme-react/button'
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

const viewingAngles = [
    { id: 1, name: 'Radial' },
    { id: 2, name: 'Axial' }
];

const tuneModes = [
    { id: 1, name: 'He' },
    { id: 2, name: 'He Cool' },
    { id: 3, name: 'No Gas' },
    { id: 4, name: 'No Gas Cool' }
];

export interface Props {
    benchSheetId: string;
    icpBenchSheetTypeId: number;
    isReadOnly?: boolean;
}

export default function IcpInstrument(props: Props) {
    const [instrumentData, setInstrumentData] =
        useState<IcInstrumentData>();

    const [instruments, setInstruments] =
        useState<Asset[]>();

    const [instrument, setInstrument] =
        useState<Asset>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId]);

    useEffect(() => {
        getMatchingInstrument();
    }, [instruments]);

    key++;

    return (
        <div>
            <div className="lq-header pw-margin-top-05 pw-bg-green">
                <div className="lq-header-title">ICP Instrument</div>
                <div className="title-bar-spacer" />
                <div>
                    <Button className="title-bar-button"
                        text="" icon="info" type="success"
                        hint="item information"
                        onClick={showInformationDialog} />
                </div>
            </div>

            <SecondaryBenchSheetInstrument
                benchSheetId={props.benchSheetId}
                isReadOnly={props.isReadOnly}
                technology={BenchSheetTechnologyType.InductivelyCoupledPlasmaSpectroscopy}
                showHeader={false}
            />

            <div className="pw-form-section">
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Element</div>
                        <TextBox id="element" readOnly={props.isReadOnly}
                            placeholder='element'
                            className="asset-selectbox"
                            height='2.1em'
                            onValueChanged={onValueChanged}
                            value={instrumentData && instrumentData.element ?
                                instrumentData.element : undefined} />
                    </div>

                    {props.icpBenchSheetTypeId === IcpBenchSheetType.OES &&
                        <>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Wavelength(nm)</div>
                                <NumberBox id="wavelength" readOnly={props.isReadOnly}
                                    placeholder='mass'
                                    className="asset-selectbox"
                                    height='2.1em'
                                    onValueChanged={onValueChanged}
                                    value={instrumentData && instrumentData.wavelength ?
                                        instrumentData.wavelength : undefined} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Viewing Angle</div>
                                <SelectBox id="viewingAngle" readOnly={props.isReadOnly}
                                    placeholder='viewing angle'
                                    valueExpr='id' displayExpr='name'
                                    className="asset-selectbox"
                                    height='2.1em'
                                    dataSource={viewingAngles}
                                    onValueChanged={onValueChanged}
                                    value={instrumentData && instrumentData.viewingAngle ?
                                        instrumentData.viewingAngle : undefined} />
                            </div>
                        </>
                    }

                    {props.icpBenchSheetTypeId === IcpBenchSheetType.MS &&
                        <>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Mass</div>
                                <NumberBox id="mass" readOnly={props.isReadOnly}
                                    placeholder='mass'
                                    className="asset-selectbox"
                                    height='2.1em'
                                    onValueChanged={onValueChanged}
                                    value={instrumentData && instrumentData.mass ?
                                        instrumentData.mass : undefined} />
                            </div>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Tune Mode</div>
                                <SelectBox id="tuneMode" readOnly={props.isReadOnly}
                                    placeholder='tune mode'
                                    valueExpr='id' displayExpr='name'
                                    className="asset-selectbox"
                                    height='2.1em'
                                    dataSource={tuneModes}
                                    onValueChanged={onValueChanged}
                                    value={instrumentData && instrumentData.tuneMode ?
                                        instrumentData.tuneMode : undefined} />
                            </div>
                        </>
                    }

                </div>
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function getMatchingInstrument() {
        if (instruments && instrumentData && instrumentData.instrumentId) {
            const matchingInstrument = instruments.find(i => i.assetId === instrumentData.instrumentId);

            if (matchingInstrument)
                setInstrument(matchingInstrument);
        }
    }

    async function onValueChanged(e: any) {
        if (!dataLoaded)
            return;

        const caller = e.element.id;

        setInstrumentData({ ...instrumentData, [caller]: e.value });

        let fieldUpdate: FieldUpdate = {
            entity: "IcpBenchSheets",
            field: caller,
            value: e.value,
            rowId: props.benchSheetId
        };

        //todo: also set UOMs (which are fixed but we want in the database anyway)
        await updateIcpBenchSheetField(fieldUpdate)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating ICP Bench Sheet Field");
            });
    }

    async function getIcpInstrumentData() {
        await CrudFunctions.getData(`benchsheets/icpinstrumentdata/${props.benchSheetId}`)
            .then((data) => {
                setInstrumentData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet Data");
            });
    }

    // TODO
    async function getInstruments() {
        //await CrudFunctions.getData(`assets/assets?technologyType=${BenchSheetTechnologyType.InductivelyCoupledPlasmaSpectroscopy}`)
        //    .then(data => {
        //        setInstruments(data);
        //    })
        //    .catch((res) => {
        //        showMessage(res, ModalMessageType.Error, "Error Retrieving Instruments");
        //    });
    }

    async function updateIcpBenchSheetField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("benchsheets/icpbenchsheetfield", fieldUpdate);
    }

    async function getAllData() {
        await Promise.all([
            getInstruments(),
            getIcpInstrumentData()
        ])
            .then(() => setDataLoaded(true));
    }

    function showInformationDialog() {
        if (instrument) {
            let itemBasicInformation: ItemBasicInformation = {
                rowPointer: instrument.assetId,
                createTimestamp: instrument.createTimestamp,
                lastModifiedTimestamp: instrument.modifyTimestamp,
                createUser: instrument.createUser,
                modifyUser: instrument.modifyUser
            };

            setItemBasicInformation(itemBasicInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }
    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
